import { WarningIcon } from '@plato/ui-legacy/assets/icons/warning-icon';
import { BaseConfirmationDialog } from '@plato/ui-legacy/components/dialog/confirmation-dialog';
import { DialogStateManager, useDialog } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { RadioList, RadioListItem } from '@plato/ui-legacy/components/ui/button-radio-list';
import { useToast } from '@plato/ui-legacy/components/ui/use-toast';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { CrmEventEditScope } from '../../../server/queries/utils/crm-event-build-input.ts';
import type { RouterOutputs } from '../../../utils/api.ts';
import type { ModalInnerProps } from '../../modal/index.tsx';
import { useDeleteEvent } from '../hooks/use-delete-event.ts';
export type DeleteEventModalProps = {
    onDeleted?: () => void;
    date: string;
    event: RouterOutputs['platoUser']['query']['events']['getEventById'];
} & ModalInnerProps;

export const DeleteEventModal = DialogStateManager.create<DeleteEventModalProps>(
    ({ onDeleted, event, date: occurrenceDate }: DeleteEventModalProps) => {
        const { t } = useTranslation();
        const { toast } = useToast();
        const modal = useDialog();
        const [deleteScope, setDeleteScope] = React.useState<CrmEventEditScope>('single');

        const isRecurring = Boolean(event.recurrenceRule);

        const handleDeleteSuccess = () => {
            toast({
                icon: 'success',
                title: t('events__delete_success', { ns: 'events' }),
            });
            onDeleted?.();
            modal.remove();
        };

        const handleDeleteError = (message?: string) => {
            toast({
                icon: 'warning',
                title: t('events__delete_error', { ns: 'events' }),
                description: message,
            });
        };

        const { mutate: deleteEvent, isPending: isDeleting } = useDeleteEvent(event, {
            onSuccess: handleDeleteSuccess,
            onError: handleDeleteError,
        });

        const handleDelete = () => {
            deleteEvent({ scope: deleteScope, occurrenceDate });
        };

        const handleCancel = () => {
            modal.remove();
        };

        return (
            <BaseConfirmationDialog
                focus="destroy"
                title={t('events__delete__title', { ns: 'events' })}
                description={t('events__delete_confirmation__body', {
                    ns: 'events',
                })}
                icon={WarningIcon}
            >
                {isRecurring && (
                    <div className="self-start">
                        <RadioList
                            value={deleteScope}
                            onValueChange={(val) => {
                                setDeleteScope(val as CrmEventEditScope);
                            }}
                        >
                            <RadioListItem
                                value="single"
                                label={t('events__recurrence_scope__single', { ns: 'events' })}
                            />
                            <RadioListItem
                                value="following"
                                label={t('events__recurrence_scope__following', { ns: 'events' })}
                            />
                        </RadioList>
                    </div>
                )}
                <BaseConfirmationDialog.Actions>
                    <BaseConfirmationDialog.RejectButton onClick={handleCancel}>
                        {t('form__cancel')}
                    </BaseConfirmationDialog.RejectButton>
                    <BaseConfirmationDialog.AcceptButton onClick={handleDelete} isLoading={isDeleting}>
                        {t('events__delete', { ns: 'events' })}
                    </BaseConfirmationDialog.AcceptButton>
                </BaseConfirmationDialog.Actions>
            </BaseConfirmationDialog>
        );
    },
);
